import { ButtonGroup } from "@tql/ui-components-react/kendo/buttons";
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	phoneCall_01,
	truck_01,
} from "@tql/ui-components-react/untitled/icons";
import { useState } from "react";
import { useParams } from "react-router";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { dateFormatter } from "~/shared/utils/date-formatter.ts";

import { usePostApiDispatch } from "../../hooks/use-post-api-dispatch.ts";
import type { AugmentedShipment } from "../../types/augmented-shipment";
import { isQuoteExpired } from "../../utils/is-quote-expired.ts";
import { DispatchErrorModal } from "../dispatch-error-modal/index.tsx";
import { PhoneDispatchModal } from "./phone-dispatch-modal.tsx";

export const NotDispatchedGroup = ({
	shipment,
}: {
	shipment: AugmentedShipment;
}) => {
	const { isInternal } = useDeploymentType();
	const params = useParams();
	const [show, setShow] = useState(false);

	const { apiDispatch, isSaving, dispatchError, clearDispatchError } =
		usePostApiDispatch();
	// Only reflect this group's own dispatch action in the global spinner. The
	// dispatch hooks keep their mutation pending through the shipment refetch, so
	// the overlay stays until the state transitions. Reacting to isFetching here
	// would also show the full-page spinner for unrelated background refetches
	// (e.g. acknowledging an exception), on top of that action's own spinner.
	useAutoSpinner(isSaving);
	const internalBtnGroup = (
		<>
			<PhoneDispatchModal
				customerId={shipment.customer.id}
				isOpen={show}
				onClose={() => setShow(false)}
				poNumber={params["poNumber"] ?? ""}
			/>
			<DispatchErrorModal
				attemptedAt={dispatchError?.attemptedAt ?? null}
				errorMessage={dispatchError?.message}
				isOpen={dispatchError != null}
				onClose={clearDispatchError}
			/>
			<ButtonGroup className={"shipment-button-group"}>
				{/* An expired quote must be rerated before API dispatch; phone dispatch stays available. */}
				<Button
					data-testid={"api-dispatch-trigger"}
					disabled={isQuoteExpired(shipment)}
					onClick={() =>
						apiDispatch({
							poNumber: params["poNumber"] ?? "",
							customerId: shipment?.customer?.id,
						})
					}
				>
					<SvgIcon className={"shipment-progress-icon"} icon={truck_01} />
					API Dispatch
				</Button>
				<Button onClick={() => setShow(true)}>
					<SvgIcon className={"shipment-progress-icon"} icon={phoneCall_01} />
					Phone Dispatch
				</Button>
			</ButtonGroup>
		</>
	);
	const externalTextGroup = (
		<div className={"not-dispatched-text"}>
			<div>Ship Date:</div>
			<div>
				{dateFormatter(shipment?.shipmentDetails?.pickup?.scheduled ?? "") ??
					"N/A"}
			</div>
		</div>
	);
	return isInternal ? internalBtnGroup : externalTextGroup;
};
